/**
 * Portal Reports Page
 * Financial reports for business users
 */

import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { PermissionGate } from '../../components/PermissionGate';
import portalService from '../../services/portal.service';
import type { PortalReportSummary } from '../../types/portal.types';

const fmtECV = (n?: number) =>
  Math.round(n ?? 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 });

const PortalReports: React.FC = () => {
  const { hasPermission } = useAuth();
  const [summary, setSummary] = useState<PortalReportSummary | null>(null);
  const [dailyData, setDailyData] = useState<any[]>([]);
  const [serviceData, setServiceData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [periodo, setPeriodo] = useState(30);
  const [activeTab, setActiveTab] = useState<'resumo' | 'diario' | 'servicos'>('resumo');

  const loadData = async () => {
    try {
      setLoading(true);
      const [summaryRes, dailyRes, serviceRes] = await Promise.all([
        portalService.getReportSummary({ dias: periodo }),
        portalService.getReportDaily(periodo),
        portalService.getReportByServices({ dias: periodo }),
      ]);
      setSummary(summaryRes as any);
      setDailyData(dailyRes || []);
      setServiceData(serviceRes || []);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar relatorios');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [periodo]);

  const handleExport = async (tipo: string, formato: string) => {
    try {
      const result = await portalService.exportReport(tipo, formato, { dias: periodo });
      if (result.url) {
        window.open(result.url, '_blank');
      } else if (result.data) {
        const blob = new Blob([result.data], {
          type: formato === 'csv' ? 'text/csv' : 'application/pdf'
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `relatorio_${tipo}_${new Date().toISOString().split('T')[0]}.${formato}`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err: any) {
      setError(err.message || 'Erro ao exportar relatorio');
    }
  };

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pagali-primary"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Relatórios</h1>
          <p className="text-gray-600">Analise financeira do seu negocio</p>
        </div>
        <div className="flex gap-4">
          <select
            value={periodo}
            onChange={(e) => setPeriodo(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pagali-primary"
          >
            <option value={7}>Últimos 7 dias</option>
            <option value={30}>Últimos 30 dias</option>
            <option value={90}>Últimos 90 dias</option>
            <option value={365}>Último ano</option>
            <option value={3650}>Todo o período</option>
          </select>
          <PermissionGate permission="MY_REPORTS_EXPORT">
            <div className="relative group">
              <button className="px-4 py-2 bg-pagali-primary text-white rounded-lg hover:bg-pagali-primary/80 flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Exportar
              </button>
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                <button
                  onClick={() => handleExport('resumo', 'csv')}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-t-lg"
                >
                  Exportar CSV
                </button>
                <button
                  onClick={() => handleExport('resumo', 'pdf')}
                  className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 rounded-b-lg"
                >
                  Exportar PDF
                </button>
              </div>
            </div>
          </PermissionGate>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-600">{error}</p>
          <button onClick={() => setError(null)} className="text-sm text-red-500 underline mt-1">
            Fechar
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">Total Recebido</p>
            <p className="text-2xl font-semibold text-green-600">
              {fmtECV(summary.financeiro?.totalRecebido)} ECV
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {summary.financeiro?.totalTransacoes} transacoes
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">Ticket Medio</p>
            <p className="text-2xl font-semibold">
              {fmtECV(summary.financeiro?.ticketMedio)} ECV
            </p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">Clientes Unicos</p>
            <p className="text-2xl font-semibold">{summary.financeiro?.clientesUnicos}</p>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <p className="text-sm text-gray-500">Falhados / Estornados</p>
            <p className="text-2xl font-semibold">
              <span className="text-red-600">
                {fmtECV(summary.financeiro?.totalFalhado)}
              </span>
              {' / '}
              <span className="text-purple-600">
                {fmtECV(summary.financeiro?.totalEstornado)}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-8">
          <button
            onClick={() => setActiveTab('resumo')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'resumo'
                ? 'border-pagali-primary text-pagali-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Resumo por Estado
          </button>
          <button
            onClick={() => setActiveTab('diario')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'diario'
                ? 'border-pagali-primary text-pagali-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Diario
          </button>
          <button
            onClick={() => setActiveTab('servicos')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'servicos'
                ? 'border-pagali-primary text-pagali-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Por Servico
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'resumo' && summary?.porEstado && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Transacoes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Valor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  % do Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {summary.porEstado.map((item, index) => {
                const totalMeta = Number(summary.financeiro?.totalMeta || 0);
                const totalValor = summary.porEstado.reduce((sum, i) => sum + Number(i.valor), 0);
                const base = totalMeta > 0 ? totalMeta : totalValor;
                const percent = base > 0 ? ((Number(item.valor) / base) * 100).toFixed(2) : 0;
                return (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        item.ESTADO === 'S' ? 'bg-green-100 text-green-800' :
                        item.ESTADO === 'N' ? 'bg-yellow-100 text-yellow-800' :
                        item.ESTADO === 'F' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {item.estadoDescricao}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fmtECV(item.valor)} ECV
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-20 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-pagali-primary h-2 rounded-full"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500">{percent}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'diario' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Transacoes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Valor Total
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {dailyData.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-12 text-center text-gray-500">
                    Nenhum dado encontrado para o periodo.
                  </td>
                </tr>
              ) : (
                dailyData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(() => {
                        try {
                          // item.data is always YYYY-MM-DD string from backend
                          const d = new Date(String(item.data).split('T')[0] + 'T12:00:00Z');
                          return isNaN(d.getTime()) ? String(item.data) : d.toLocaleDateString('pt-CV');
                        } catch { return String(item.data); }
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.transacoes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fmtECV(item.valor)} ECV
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === 'servicos' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Servico
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Transacoes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Ticket Medio
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {serviceData.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center text-gray-500">
                    Nenhum dado encontrado para o periodo.
                  </td>
                </tr>
              ) : (
                serviceData.map((item, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.servico}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.transacoes}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {fmtECV(item.valor)} ECV
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {fmtECV(item.transacoes > 0 ? item.valor / item.transacoes : 0)} ECV
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PortalReports;
